const SESSION_QS = 'session';
const SESSION_STORAGE_KEY = 'pulse.activeSession';
const AGENT_QS = 'agent';
/** Admin preview pin: working | positive version number. */
const AGENT_VERSION_QS = 'av';
/** RCA / Test call: which remote audio to play when LiveAvatar is present. */
const AVATAR_AUDIO_QS = 'avatarAudio';

export type AvatarAudioPath = 'auto' | 'liveavatar' | 'tts';

export function readSessionFromUrl(): string | null {
  try {
    const v = new URLSearchParams(window.location.search).get(SESSION_QS);
    return v?.trim() || null;
  } catch {
    return null;
  }
}

export function readAgentFromUrl(search?: string): string | null {
  try {
    const v = new URLSearchParams(search ?? window.location.search).get(AGENT_QS);
    return v?.trim() || null;
  } catch {
    return null;
  }
}

/** Admin Test call pin — `working` or published version number. */
export function readAgentVersionFromUrl(search?: string): 'working' | number | null {
  try {
    const raw = new URLSearchParams(search ?? window.location.search).get(AGENT_VERSION_QS)?.trim();
    if (!raw) return null;
    if (raw === 'working' || raw === '0') return 'working';
    if (/^\d+$/.test(raw)) {
      const n = Number(raw);
      return n >= 1 ? n : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Remote audio path for avatar calls (`?avatarAudio=`).
 * - auto (default): LiveAvatar audio when that participant is in the room, else bot TTS
 * - liveavatar: only LiveAvatar audio (mute bot mic)
 * - tts: only bot Cartesia (mute LiveAvatar audio; video may still show)
 */
export function readAvatarAudioFromUrl(search?: string): AvatarAudioPath {
  try {
    const raw = new URLSearchParams(search ?? window.location.search)
      .get(AVATAR_AUDIO_QS)
      ?.trim()
      .toLowerCase();
    if (raw === 'liveavatar' || raw === 'avatar') return 'liveavatar';
    if (raw === 'tts' || raw === 'bot') return 'tts';
    return 'auto';
  } catch {
    return 'auto';
  }
}

/** Keep selected query keys when changing path (e.g. /?agent=Emma → /haircheck-returning?agent=Emma). */
export function pathWithKeptQuery(
  path: string,
  search?: string | null,
  keys: string[] = [AGENT_QS, AGENT_VERSION_QS, AVATAR_AUDIO_QS],
): string {
  try {
    const src = new URLSearchParams(search ?? window.location.search);
    const next = new URLSearchParams();
    for (const k of keys) {
      const v = src.get(k)?.trim();
      if (v) next.set(k, v);
    }
    const q = next.toString();
    return q ? `${path}?${q}` : path;
  } catch {
    return path;
  }
}

export function writeSessionToUrl(
  sessionId: string,
  agentId?: string | null,
  agentVersion?: 'working' | number | null,
  avatarAudio?: AvatarAudioPath | null,
) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(SESSION_QS, sessionId);
    if (agentId) url.searchParams.set(AGENT_QS, agentId);
    if (agentVersion === 'working') url.searchParams.set(AGENT_VERSION_QS, 'working');
    else if (typeof agentVersion === 'number' && agentVersion >= 1) {
      url.searchParams.set(AGENT_VERSION_QS, String(agentVersion));
    }
    // Preserve non-default RCA override across join navigation.
    if (avatarAudio && avatarAudio !== 'auto') {
      url.searchParams.set(AVATAR_AUDIO_QS, avatarAudio);
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    /* ignore */
  }
}

export function clearSessionIdentity() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(SESSION_QS)) {
      url.searchParams.delete(SESSION_QS);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Prefer URL (shareable/refresh), then sessionStorage. */
export function resolveResumeSessionId(): string | null {
  return readSessionFromUrl() || (() => {
    try {
      return sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  })();
}
